import { _isNumberValue } from '@ngx-simple/core/coercion';
import { isFunction, MAX_SAFE_INTEGER } from '@ngx-simple/core/typeof';
import { SimPageEvent, SimPaginatorComponent } from '@ngx-simple/simple-ui/paginator';
import { SimSortChange, SimSortDirective } from '@ngx-simple/simple-ui/sort';
import { BehaviorSubject, combineLatest, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SimDataSource } from './data-source';

export class SimTableDataChange {
  pageIndex?: number;
  pageSize?: number;
  sort?: {
    /** 正在排序的列的id */
    active: string;

    /** 正在排序的方向 */
    direction: 'asc' | 'desc' | '';
    /**
     * 正在排序的方向值
     * - asc => 1
     * - desc => -1
     * - '' => 0
     */
    sort: number;
  };
  constructor(sort?: SimSortDirective, paginator?: SimPaginatorComponent, public filter?: string) {
    if (sort) {
      const simSortDirection = sort.simSortDirection;
      this.sort = {
        active: sort.simSortActive,
        direction: simSortDirection,
        sort: simSortDirection === '' ? 0 : simSortDirection === 'asc' ? 1 : -1
      };
    }
    if (paginator) {
      this.pageIndex = paginator.pageIndex;
      this.pageSize = paginator.pageSize;
    }
  }
}

/**
 * 本地分页排序筛选表格数源
 */
export class SimTableDataSource<T> implements SimDataSource<T> {
  private readonly _data: BehaviorSubject<T[]>;

  private readonly _renderData = new BehaviorSubject<T[]>([]);

  /** 在数据源上设置新的筛选器字符串时发出的流 */
  private readonly _filter = new BehaviorSubject<string>('');

  /** 该表应呈现的数据数组，其中每个对象代表一行。 */
  get data() {
    return this._data.value;
  }
  set data(data: T[]) {
    this._data.next(data);
  }

  /**
   * 用于从数据数组中过滤出对象的筛选术语。若要覆盖数据对象与此筛选器字符串匹配的方式，请为filterPredicate提供一个自定义函数。
   */
  get filter(): string {
    return this._filter.value;
  }
  set filter(filter: string) {
    this._filter.next(filter);
  }

  get sort(): SimSortDirective | null {
    return this._sort;
  }
  set sort(sort: SimSortDirective | null) {
    this._sort = sort;
    this._updateChangeSubscription();
  }
  private _sort: SimSortDirective | null;

  get paginator(): SimPaginatorComponent | null {
    return this._paginator;
  }
  set paginator(paginator: SimPaginatorComponent | null) {
    this._paginator = paginator;
    if (this._paginator) {
      this._paginator.pageTotal = this.data.length;
    }
    this._updateChangeSubscription();
  }
  private _paginator: SimPaginatorComponent | null;

  private readonly _internalPageChanges = new Subject<void>();

  /**
   * 订阅应该触发对表呈现行的更新的更改，如筛选、排序、分页或基本数据更改。
   */
  _renderChangesSubscription = Subscription.EMPTY;

  /** 已由筛选器字符串匹配的筛选数据集，或如果没有筛选器，则为所有数据。 */
  filteredData: T[];

  /**
   * 显示加载效果
   * @example
   *
   * pipe(indicate(SimTableDataSource.loading$))
   */
  loading$ = new Subject<boolean>();

  /**
   * @param initialData 初始化数据
   */
  constructor(initialData: T[] = [], public page?: { index?: number; size?: number; sizeOptions?: number[]; auto?: boolean }) {
    this._data = new BehaviorSubject<T[]>(initialData);

    this._updateChangeSubscription();
  }

  /**
   * 检查数据对象是否与数据源的筛选器字符串匹配。
   * @param data 用于检查筛选器的数据对象
   * @param filter 在数据源上设置的筛选字符串
   * @returns 过滤器是否与数据匹配
   */
  filterPredicate: (data: T, filter: string) => boolean;

  /**
   * filterChange、sortChange、pageChange 任意改变发出事件
   */
  readonly change: Subject<SimTableDataChange> = new Subject();

  connect() {
    return this._renderData;
  }

  disconnect() {}

  /**
   * 排序数据 可以定制
   * @param data 用于排序的数据对象
   * @param sort 在数据源上设置的筛选字符串
   * @returns 排序结果数据
   */
  sortData: (data: T[], sort: SimSortDirective) => T[] = (data: T[], sort: SimSortDirective): T[] => {
    const active = sort.simSortActive;
    const direction = sort.simSortDirection;
    if (!active || direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const valueA = this.sortingDataAccessor(a, active);
      const valueB = this.sortingDataAccessor(b, active);

      // 如果valueA和valueB都存在，则将两者进行比较。
      // 否则，检查一个值是否存在，而另一个不存在。在这种情况下，现有值应排在最后。
      // 在将值与`undefined/null`进行比较时，这避免了不一致的结果。
      // 如果两个值都不存在，则返回0。
      let comparatorResult = 0;
      if (valueA != null && valueB != null) {
        // 检查一个值是否大于另一个值;如果相等，comparatorResult应保持为0
        if (valueA > valueB) {
          comparatorResult = 1;
        } else if (valueA < valueB) {
          comparatorResult = -1;
        }
      } else if (valueA != null) {
        comparatorResult = 1;
      } else if (valueB != null) {
        comparatorResult = -1;
      }

      return comparatorResult * (direction === 'asc' ? 1 : -1);
    });
  };

  private _updateChangeSubscription() {
    const sortChange: Observable<SimSortChange | null | void> = this._sort
      ? (merge(this._sort.sortChange, this._sort.initialized) as Observable<SimSortChange | void>)
      : of(null);

    const pageChange: Observable<SimPageEvent | null | void> = this._paginator
      ? (merge(this._paginator.pageChange, this._internalPageChanges, this._paginator.initialized) as Observable<SimPageEvent | void>)
      : of(null);

    const dataStream = this._data;
    // 观察基本数据或筛选器更改，以提供一组筛选后的数据。
    const filteredData = combineLatest([dataStream, this._filter.pipe(tap(v => !!v && this._emitChangeEvent()))]).pipe(
      map(([data]) => this._filterData(data))
    );
    // 观察筛选后的数据或排序更改，以提供一组有序的数据。
    const orderedData = combineLatest([filteredData, sortChange.pipe(tap(v => !!v && this._emitChangeEvent()))]).pipe(
      map(([data]) => this._orderData(data))
    );
    // 查看有序数据或页面更改以提供分页数据集。
    const paginatedData = combineLatest([orderedData, pageChange.pipe(tap(v => !!v && this._emitChangeEvent()))]).pipe(
      map(([data]) => this._pageData(data))
    );
    // 观察分页数据的变化，并将结果发送到表中进行渲染。
    this._renderChangesSubscription.unsubscribe();
    this._renderChangesSubscription = paginatedData.subscribe(data => this._renderData.next(data));
  }

  /** 返回筛选后的数据数组，其中每个筛选对象都包含筛选器函数结果中的筛选器字符串。如果没有设置过滤器，则返回所提供的数据数组。 */
  private _filterData(data: T[]): T[] {
    if (!this.filter) {
      return data;
    }
    // 如果没有提供谓词方法 抛出异常
    if (!isFunction(this.filterPredicate)) {
      throw Error(`filter must be provided with a filterPredicate function.`);
    }
    this.filteredData = data.filter(obj => this.filterPredicate(obj, this.filter));

    if (this.paginator) {
      this._updatePaginator(this.filteredData.length);
    }

    return this.filteredData;
  }

  /** 根据提供的SimPaginator的页面索引和长度返回提供的数据数组的分页片。如果没有提供分页器，则返回所提供的数据数组。 */
  private _pageData(data: T[]): T[] {
    if (!this.paginator) {
      return data;
    }

    // 本地分页
    const startIndex = (this.paginator.pageIndex - 1) * this.paginator.pageSize;
    return data.slice(startIndex, startIndex + this.paginator.pageSize);
  }

  private _orderData(data: T[]): T[] {
    // 如果没有活动排序或方向，返回数据而不尝试排序。
    if (!this.sort) {
      return data;
    }

    return this.sortData(data.slice(), this.sort);
  }

  sortingDataAccessor: (data: T, sortHeaderId: string) => string | number = (data: T, sortHeaderId: string): string | number => {
    const value = (data as { [key: string]: any })[sortHeaderId];

    if (_isNumberValue(value)) {
      const numberValue = Number(value);

      // 无法可靠地比较超出`MAX_SAFE_INTEGER`的数字，因此我们将其保留为字符串。
      // see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/MAX_SAFE_INTEGER
      return numberValue < MAX_SAFE_INTEGER ? numberValue : value;
    }

    return value;
  };

  private _updatePaginator(filteredDataLength: number) {
    Promise.resolve().then(() => {
      const paginator = this.paginator;

      if (!paginator) {
        return;
      }

      paginator.pageTotal = filteredDataLength;

      // 如果页面索引设置在页面之外，则将其减少到最后一页。
      if (paginator.pageIndex > 0) {
        const lastPageIndex = Math.ceil(paginator.pageTotal / paginator.pageSize) - 1 || 0;
        const newPageIndex = Math.min(paginator.pageIndex, lastPageIndex);

        if (newPageIndex !== paginator.pageIndex) {
          paginator.pageIndex = newPageIndex;

          // 由于paginator只在用户生成更改后发送，所以我们需要自己的流，这样我们就知道应该重新呈现数据。
          this._internalPageChanges.next();
        }
      }
    });
  }

  /** 发出一个事件，通知已触发paginator属性的更改。 */
  private _emitChangeEvent() {
    this.change.next(new SimTableDataChange(this.sort, this.paginator, this.filter));
  }
}
